import { IBusinessDetails } from '@forwardflow/types/onboarding'
import { TextInput, Grid, Stack, Group, Radio } from '@forwardflow/ui-v2'
import { useState } from 'react'

export const BusinessDetailsForm = ({
   businessDetails,
   disabled = false,
}: {
   businessDetails?: IBusinessDetails | null
   disabled?: boolean
}) => {
   const [values, setValues] = useState<Partial<IBusinessDetails>>({
      legalBusinessName: businessDetails?.legalBusinessName || '',
      businessOwnerName: businessDetails?.businessOwnerName || '',
      businessOwnerTitle: businessDetails?.businessOwnerTitle || '',
      websiteUrl: businessDetails?.websiteUrl || '',
      businessEIIN: businessDetails?.businessEIIN || '',
      street: businessDetails?.street || '',
      city: businessDetails?.city || '',
      state: businessDetails?.state || '',
      postalCode: businessDetails?.postalCode || '',
      businessTeamName: businessDetails?.businessTeamName || '',
      businessBrokerageName: businessDetails?.businessBrokerageName || '',
      hasTeam: businessDetails?.hasTeam || false,
   })

   const [errors, setErrors] = useState<
      Partial<Record<keyof IBusinessDetails, string>>
   >({})

   const validateBusinessOwnerName = (name: string): string => {
      if (!name) return ''
      const nameRegex = /^[a-zA-Z\s.]+$/
      if (!nameRegex.test(name)) {
         return 'Business Owner Name can only consist alphabets, period or space.'
      }
      return ''
   }

   const validateWebsiteUrl = (url: string): string => {
      if (!url) return ''
      if (url.includes(' ')) {
         return 'Enter a valid website URL.'
      }
      // Must have format xxxx.xx (domain.extension)
      const urlRegex = /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w- ./?%&=]*)?$/
      if (!urlRegex.test(url)) {
         return 'Enter a valid website URL.'
      }
      return ''
   }

   const validateEIN = (ein: string): string => {
      if (!ein) return ''
      const cleanEIN = ein.replace(/[-\s]/g, '')
      if (!/^\d+$/.test(cleanEIN)) {
         return 'Enter a valid 9-digit EIN.'
      }
      if (cleanEIN.length !== 9) {
         return 'Enter a valid 9-digit EIN.'
      }
      return ''
   }

   const validatePostalCode = (zip: string): string => {
      if (!zip) return ''
      if (!/^\d+$/.test(zip)) {
         return 'ZIP should only be numbers, and 5 digits.'
      }
      if (zip.length !== 5) {
         return 'ZIP should only be numbers, and 5 digits.'
      }
      return ''
   }

   const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target
      setValues({ ...values, [name]: value })

      // Validate on change
      let error = ''
      if (name === 'businessOwnerName') {
         error = validateBusinessOwnerName(value)
      } else if (name === 'websiteUrl') {
         error = validateWebsiteUrl(value)
      } else if (name === 'businessEIIN') {
         error = validateEIN(value)
      } else if (name === 'postalCode') {
         error = validatePostalCode(value)
      }

      setErrors({ ...errors, [name]: error })
   }

   return (
      <Stack gap={24} className="p-6">
         <TextInput
            readOnly={disabled}
            value={values.legalBusinessName}
            label="Legal business name"
            placeholder="Forward Flow Real Estate LLC"
            name="legalBusinessName"
            onChange={handleChange}
         />

         <Grid>
            <Grid.Col span={6}>
               <TextInput
                  readOnly={disabled}
                  value={values.businessOwnerName}
                  label="Business owner"
                  placeholder="John Smith"
                  name="businessOwnerName"
                  onChange={handleChange}
                  error={errors.businessOwnerName}
               />
            </Grid.Col>
            <Grid.Col span={6}>
               <TextInput
                  readOnly={disabled}
                  value={values.businessOwnerTitle}
                  label="Your title"
                  placeholder="Agent, Broker, etc."
                  name="businessOwnerTitle"
                  onChange={handleChange}
               />
            </Grid.Col>
         </Grid>

         <Stack>
            <p className="text-white text-sm font-medium">
               Are you part of a Team?
            </p>
            <Group>
               <Group className="rounded-lg border border-white/[0.1] bg-white/[0.02] px-4 py-2">
                  <Radio
                     name="hasTeam"
                     value="yes"
                     disabled={disabled}
                     checked={values.hasTeam}
                     onChange={() => setValues({ ...values, hasTeam: true })}
                  />
                  <p className="text-white text-sm font-medium">Yes</p>
               </Group>

               <Group className="rounded-lg border border-white/[0.1] bg-white/[0.02] px-4 py-2">
                  <Radio
                     name="hasTeam"
                     value="no"
                     disabled={disabled}
                     checked={!values.hasTeam}
                     onChange={() => setValues({ ...values, hasTeam: false })}
                  />
                  <p className="text-white text-sm font-medium">No</p>
               </Group>
            </Group>
         </Stack>

         {values.hasTeam && (
            <TextInput
               readOnly={disabled}
               value={values.businessTeamName}
               label="Team name"
               placeholder="Forward Team"
               name="businessTeamName"
               onChange={handleChange}
            />
         )}

         <TextInput
            readOnly={disabled}
            value={values.businessBrokerageName}
            label="Brokerage name"
            placeholder="FF Brokerage"
            name="businessBrokerageName"
            onChange={handleChange}
         />

         <TextInput
            readOnly={disabled}
            value={values.websiteUrl}
            label="Website"
            placeholder="https://www.ffrealestate.com"
            name="websiteUrl"
            onChange={handleChange}
            error={errors.websiteUrl}
         />

         <TextInput
            readOnly={disabled}
            value={values.businessEIIN}
            label="Business EIN"
            placeholder="12-3456789"
            name="businessEIIN"
            description="We need your EIN for A2P registration - it's required to send verified text messages. You can skip this for now and add it anytime later from Settings → Business Info."
            onChange={handleChange}
            error={errors.businessEIIN}
         />
      </Stack>
   )
}
